import { Fragment, useState } from "react";
import {
  BAR_CLASSES,
  INFO_LABEL,
  INFO_STRIP,
  OverUnder,
  barBand,
  fmt,
  margin,
} from "./utils";
import { EmptyState } from "../../../components/UI/States";

const MARGIN_VS_CLASSES = {
  above: "bg-[#c8f0a0] text-[#1a4a00] dark:bg-green-500/25 dark:text-green-200",
  within: "bg-[#ffffa0] text-[#555500] dark:bg-yellow-500/25 dark:text-yellow-100",
  below: "bg-[#ffc0c0] text-[#700000] dark:bg-red-500/25 dark:text-red-200",
};

const DETAIL_TD =
  "whitespace-nowrap border-b border-r border-[color:var(--app-divider)] px-2 py-1 text-right";

const MarginBar = ({ band, children }) => (
  <span
    className={`inline-flex h-4 min-w-[60px] items-center justify-center whitespace-nowrap rounded-[3px] px-[6px] text-[11px] font-bold ${BAR_CLASSES[band]}`}
  >
    {children}
  </span>
);

const CustomerTab = ({ custList, overallCo, info }) => {
  const [openIds, setOpenIds] = useState(() => new Set());

  if (!custList.length) {
    return <EmptyState compact title="No data returned." />;
  }

  const toggle = (id) =>
    setOpenIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  return (
    <div>
      <div className={INFO_STRIP}>
        <span>
          <strong className={INFO_LABEL}>Rep:</strong> {info.repName}
        </span>
        <span>
          <strong className={INFO_LABEL}>Period:</strong> {info.fromDate} → {info.toDate}
        </span>
        <span>
          <strong className={INFO_LABEL}>Company Margin:</strong> {fmt.pct(overallCo)}
        </span>
        <span>
          <strong className={INFO_LABEL}>Customers:</strong> {custList.length}
        </span>
      </div>
      <p className="app-muted -mt-1 pb-2 text-xs">
        Click a customer row to expand the product detail.
      </p>

      {custList.map((cust) => {
        const custMargin = margin(cust.totalSales, cust.totalCost);
        const diff = custMargin != null ? custMargin - overallCo : null;
        let vsKey = "within";
        if (diff != null) {
          if (diff > 2) vsKey = "above";
          else if (diff < -2) vsKey = "below";
        }
        const vsLabel =
          diff != null
            ? `${fmt.pct(custMargin)} VS ${fmt.pct(overallCo)} (${
                diff >= 0 ? "+" : ""
              }${diff.toFixed(2)}%)`
            : "—";
        const open = openIds.has(cust.id);
        const pg1s = Object.values(cust.pg1s).sort((a, b) => a.id - b.id);

        return (
          <div
            key={cust.id}
            className="app-surface mb-2 overflow-hidden rounded-xl border"
          >
            <button
              type="button"
              onClick={() => toggle(cust.id)}
              aria-expanded={open}
              className={`flex w-full select-none flex-wrap items-center gap-x-3 gap-y-2 px-3 py-2.5 text-left transition-colors hover:bg-[color:var(--app-hover)] ${
                open ? "app-divider border-b" : ""
              }`}
            >
              <span
                aria-hidden="true"
                className={`app-muted w-3 text-[11px] transition-transform ${
                  open ? "rotate-90" : ""
                }`}
              >
                ▶
              </span>
              <span className="min-w-0 flex-1 text-sm font-semibold">
                {cust.name}
              </span>
              <span className="app-chip whitespace-nowrap text-[10px]">
                {cust.type}
              </span>
              <span className="flex gap-4 text-xs">
                <span className="flex flex-col items-end">
                  <span className="app-muted text-[10px]">
                    Net Sales
                  </span>
                  <span className="font-bold tabular-nums">
                    {fmt.dollars(cust.totalSales)}
                  </span>
                </span>
                <span className="flex flex-col items-end">
                  <span className="app-muted text-[10px]">
                    Margin
                  </span>
                  <span
                    className={`whitespace-nowrap rounded-full px-2 text-[11px] font-semibold tabular-nums ${MARGIN_VS_CLASSES[vsKey]}`}
                  >
                    {vsLabel}
                  </span>
                </span>
              </span>
            </button>

            {open && (
              <div className="overflow-x-auto">
              <table className="w-full border-collapse text-xs">
                <thead>
                  <tr>
                    <th className="min-w-[220px] whitespace-nowrap border-r border-[#444] bg-[#5a5a5a] px-2 py-[5px] text-left text-white dark:bg-slate-700">
                      Product Group
                    </th>
                    {["Net Sales", "Over/Under", "Target", "Rep Margin"].map(
                      (label) => (
                        <th
                          key={label}
                          className="whitespace-nowrap border-r border-[#444] bg-[#5a5a5a] px-2 py-[5px] text-right text-white dark:bg-slate-700"
                        >
                          {label}
                        </th>
                      )
                    )}
                  </tr>
                </thead>
                <tbody>
                  {pg1s.map((pg1) => {
                    const pg1Margin = margin(pg1.sales, pg1.cost);
                    const pg1Band =
                      pg1Margin != null
                        ? pg1Margin > pg1.companyMargin + 2
                          ? "green"
                          : pg1Margin < pg1.companyMargin - 2
                          ? "red"
                          : "yellow"
                        : "none";
                    const pg2s = [...pg1.pg2s].sort(
                      (a, b) => (a["PG2ID"] || 0) - (b["PG2ID"] || 0)
                    );

                    return (
                      <Fragment key={pg1.id}>
                        <tr className="bg-amber-500/10 font-bold">
                          <td className={`${DETAIL_TD} text-left`}>
                            {pg1.name}
                          </td>
                          <td className={`${DETAIL_TD} tabular-nums`}>
                            {fmt.dollars(pg1.sales)}
                          </td>
                          <td className={DETAIL_TD}>
                            <OverUnder
                              repMargin={pg1Margin}
                              targetMargin={pg1.companyMargin}
                              hasSales={pg1.sales > 0}
                            />
                          </td>
                          <td className={`${DETAIL_TD} tabular-nums`}>
                            {fmt.pct(pg1.companyMargin)}
                          </td>
                          <td className={`${DETAIL_TD} min-w-[120px]`}>
                            <MarginBar band={pg1Band}>
                              {pg1Margin != null ? fmt.pct(pg1Margin) : "No Sales"}
                            </MarginBar>
                          </td>
                        </tr>
                        {pg2s.map((pg2, i) => {
                          const hasSales = (pg2["TotalSales"] || 0) > 0;
                          const pg2Margin = hasSales
                            ? pg2["TotalMargin"] || 0
                            : null;
                          return (
                            <tr
                              key={`${pg2["PG2ID"]}-${i}`}
                              className="even:bg-[color:var(--app-hover)]"
                            >
                              <td
                                className={`${DETAIL_TD} app-muted pl-6 text-left`}
                              >
                                {pg2["PG2"] || ""}
                              </td>
                              <td className={`${DETAIL_TD} tabular-nums`}>
                                {hasSales ? fmt.dollars(pg2["TotalSales"]) : "$0"}
                              </td>
                              <td className={DETAIL_TD}>
                                <OverUnder
                                  repMargin={pg2Margin}
                                  targetMargin={pg2["PG2 Company"] || 0}
                                  hasSales={hasSales}
                                />
                              </td>
                              <td className={`${DETAIL_TD} tabular-nums`}>
                                {fmt.pct(pg2["PG2 Company"] || 0)}
                              </td>
                              <td className={`${DETAIL_TD} min-w-[120px]`}>
                                <MarginBar band={hasSales ? barBand(pg2["Color"]) : "none"}>
                                  {hasSales ? fmt.pct(pg2Margin) : "No Sales"}
                                </MarginBar>
                              </td>
                            </tr>
                          );
                        })}
                      </Fragment>
                    );
                  })}
                </tbody>
              </table>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
};

export default CustomerTab;
